import React, { useEffect, useMemo, useState } from 'react';
import { workflowAPI } from './api';


const isPresent = (value) => {
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'boolean') return value;
  return value !== null && value !== undefined;
};

const errorMessage = (error, fallback) => (
  error.response?.data?.detail || fallback
);


export const clearWorkflowInstanceCache = (userId = null) => {
  const prefix = userId
    ? `gpj:workflow-instance:${userId}:`
    : 'gpj:workflow-instance:';
  Object.keys(localStorage)
    .filter((key) => key.startsWith(prefix))
    .forEach((key) => localStorage.removeItem(key));
};


export default function WorkflowRenderer({
  workflowId,
  userId = null,
  instanceId = null,
  onNavigate = null
}) {
  const [definition, setDefinition] = useState(null);
  const [instance, setInstance] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);

  const userScope = userId || 'current-user';
  const storageKey = `gpj:workflow-instance:${userScope}:${workflowId}`;

  useEffect(() => {
    let active = true;

    const initialize = async () => {
      setLoading(true);
      setError('');
      setConflict(false);
      setDefinition(null);
      setInstance(null);
      setValues({});
      try {
        let loadedInstance = null;
        // Remove the pre-isolation key so it can never carry another user's ID
        // into the authenticated, user-scoped lookup.
        localStorage.removeItem(`gpj:workflow-instance:${workflowId}`);
        const cachedId = localStorage.getItem(storageKey);
        const rememberedId = instanceId || cachedId;
        if (rememberedId) {
          try {
            loadedInstance = (await workflowAPI.getInstance(rememberedId)).data;
          } catch (requestError) {
            const staleCachedId = !instanceId
              && [403, 404].includes(requestError.response?.status);
            if (!staleCachedId) throw requestError;
            localStorage.removeItem(storageKey);
          }
        }
        if (!loadedInstance) {
          loadedInstance = (await workflowAPI.start(workflowId)).data;
        }
        if (userId && loadedInstance.user_id !== userId) {
          localStorage.removeItem(storageKey);
          throw new Error('Workflow ownership response did not match the authenticated user.');
        }
        const loadedDefinition = (
          await workflowAPI.getDefinition(
            loadedInstance.workflow_id,
            loadedInstance.workflow_version
          )
        ).data;
        if (active) {
          localStorage.setItem(storageKey, loadedInstance.instance_id);
          setInstance(loadedInstance);
          setDefinition(loadedDefinition);
        }
      } catch (requestError) {
        if (active) {
          setError(errorMessage(requestError, 'Unable to load this workflow.'));
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    initialize();
    return () => {
      active = false;
    };
  }, [instanceId, storageKey, userId, workflowId]);

  const currentStep = useMemo(() => {
    if (!definition || !instance) return null;
    return definition.steps.find(
      (step) => step.step_id === instance.current_step_id
    ) || null;
  }, [definition, instance]);

  useEffect(() => {
    if (!currentStep || !instance) return;
    const restored = {};
    currentStep.fields.forEach((field) => {
      if (field.type === 'informational') return;
      if (Object.prototype.hasOwnProperty.call(instance.answers, field.field_id)) {
        restored[field.field_id] = instance.answers[field.field_id];
      } else if (['checkbox', 'acknowledgement'].includes(field.type)) {
        restored[field.field_id] = false;
      } else {
        restored[field.field_id] = '';
      }
    });
    setValues(restored);
    setError('');
    setConflict(false);
  }, [currentStep, instance]);

  const validatorsFor = (field) => [
    ...(field.validators || []),
    ...((currentStep?.validators || []).filter(
      (validator) => validator.field_id === field.field_id
    ))
  ];

  const fieldError = (field) => {
    const value = values[field.field_id];
    for (const validator of validatorsFor(field)) {
      if (validator.type === 'required' && !isPresent(value)) {
        return validator.message || 'This field is required.';
      }
      if (!isPresent(value)) continue;
      if (validator.type === 'min_length' && value.length < Number(validator.value)) {
        return validator.message || `Enter at least ${validator.value} characters.`;
      }
      if (validator.type === 'max_length' && value.length > Number(validator.value)) {
        return validator.message || `Enter no more than ${validator.value} characters.`;
      }
      if (validator.type === 'equals' && value !== validator.value) {
        return validator.message || 'Please confirm this value.';
      }
      if (validator.type === 'pattern') {
        try {
          if (!(new RegExp(`^(?:${validator.value})$`)).test(value)) {
            return validator.message || 'Enter a value in the requested format.';
          }
        } catch (_error) {
          return 'This field configuration is invalid.';
        }
      }
      if (validator.type === 'type') {
        const validType = {
          string: typeof value === 'string',
          boolean: typeof value === 'boolean',
          email: typeof value === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)
        }[validator.value];
        if (!validType) return validator.message || 'Enter a valid value.';
      }
    }
    return '';
  };

  const answerableFields = currentStep?.fields.filter(
    (field) => field.type !== 'informational'
  ) || [];
  const canContinue = answerableFields.every((field) => !fieldError(field));

  const setValue = (fieldId, value) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!canContinue || !instance || !currentStep) return;
    setSubmitting(true);
    setError('');
    setConflict(false);
    try {
      const answers = Object.fromEntries(
        answerableFields
          .filter((field) => (
            typeof values[field.field_id] === 'boolean'
            || isPresent(values[field.field_id])
          ))
          .map((field) => [field.field_id, values[field.field_id]])
      );
      const response = await workflowAPI.submitStep(
        instance.instance_id,
        currentStep.step_id,
        { revision: instance.revision, answers }
      );
      setInstance(response.data);
    } catch (requestError) {
      const stale = requestError.response?.status === 409;
      setConflict(stale);
      setError(errorMessage(
        requestError,
        stale
          ? 'This workflow changed elsewhere. Reload the latest progress.'
          : 'Unable to save this step.'
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const reloadLatest = async () => {
    if (!instance) return;
    setLoading(true);
    setError('');
    try {
      const response = await workflowAPI.getInstance(instance.instance_id);
      setInstance(response.data);
      setConflict(false);
    } catch (requestError) {
      setError(errorMessage(requestError, 'Unable to reload workflow progress.'));
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    const common = {
      id: field.field_id,
      name: field.field_id,
      'aria-describedby': `${field.field_id}-help ${field.field_id}-error`
    };
    const validationError = fieldError(field);

    if (field.type === 'informational') {
      return (
        <div className="gpj-workflow__information" key={field.field_id}>
          <strong>{field.label}</strong>
          {field.help_text && <p>{field.help_text}</p>}
        </div>
      );
    }

    let control;
    if (field.type === 'textarea') {
      control = (
        <textarea
          {...common}
          value={values[field.field_id] || ''}
          onChange={(event) => setValue(field.field_id, event.target.value)}
          rows="4"
        />
      );
    } else if (field.type === 'select') {
      control = (
        <select
          {...common}
          value={values[field.field_id] || ''}
          onChange={(event) => setValue(field.field_id, event.target.value)}
        >
          <option value="">Choose an option</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      );
    } else if (field.type === 'radio') {
      control = (
        <div className="gpj-workflow__choices">
          {field.options.map((option) => (
            <label key={option.value}>
              <input
                type="radio"
                name={field.field_id}
                value={option.value}
                checked={values[field.field_id] === option.value}
                onChange={(event) => setValue(field.field_id, event.target.value)}
              />
              {option.label}
            </label>
          ))}
        </div>
      );
    } else if (['checkbox', 'acknowledgement'].includes(field.type)) {
      control = (
        <label className="gpj-workflow__check">
          <input
            {...common}
            type="checkbox"
            checked={Boolean(values[field.field_id])}
            onChange={(event) => setValue(field.field_id, event.target.checked)}
          />
          <span>{field.label}</span>
        </label>
      );
    } else {
      control = (
        <input
          {...common}
          type="text"
          value={values[field.field_id] || ''}
          onChange={(event) => setValue(field.field_id, event.target.value)}
        />
      );
    }

    return (
      <div className="gpj-workflow__field" key={field.field_id}>
        {!['checkbox', 'acknowledgement'].includes(field.type) && (
          <label htmlFor={field.field_id}>{field.label}</label>
        )}
        {control}
        {field.help_text && <p id={`${field.field_id}-help`}>{field.help_text}</p>}
        {validationError && (
          <p id={`${field.field_id}-error`} className="gpj-workflow__field-error">
            {validationError}
          </p>
        )}
      </div>
    );
  };

  const renderResource = (resource) => {
    const internal = resource.resource_type === 'internal_page';
    const navigateInternal = (event) => {
      if (!internal || !onNavigate) return;
      event.preventDefault();
      onNavigate(resource.url.replace(/^\//, ''));
    };

    return (
      <article className="gpj-workflow__resource" key={`${resource.resource_id}:${resource.version}`}>
        <h3>{resource.title}</h3>
        <p>{resource.description}</p>
        <p className="gpj-workflow__resource-source">Source: {resource.source_name}</p>
        <a
          href={resource.url}
          onClick={navigateInternal}
          target={internal ? undefined : '_blank'}
          rel={internal ? undefined : 'noopener noreferrer'}
          aria-label={internal ? `Open ${resource.title}` : `Open ${resource.title} (opens in a new tab)`}
        >
          {internal ? 'Open resource' : 'Open external resource'}
          {!internal && <span aria-hidden="true"> ↗</span>}
        </a>
      </article>
    );
  };

  if (loading) {
    return <p className="gpj-loading" role="status">Loading workflow…</p>;
  }
  if (!definition || !instance) {
    return <div className="gpj-error" role="alert">{error || 'Workflow unavailable.'}</div>;
  }
  if (instance.status === 'completed') {
    return (
      <section className="gpj-section gpj-workflow">
        <div className="gpj-wrap gpj-workflow__card">
          <p className="gpj-card__kicker">Completed</p>
          <h1>{definition.title}</h1>
          <p>Your saved workflow is complete.</p>
        </div>
      </section>
    );
  }
  if (!currentStep) {
    return <div className="gpj-error" role="alert">The current workflow step is unavailable.</div>;
  }

  const stepIndex = definition.steps.findIndex(
    (step) => step.step_id === currentStep.step_id
  );
  const progress = Math.round(((stepIndex + 1) / definition.steps.length) * 100);

  return (
    <section className="gpj-section gpj-workflow">
      <div className="gpj-wrap gpj-workflow__card">
        <p className="gpj-card__kicker">{definition.title}</p>
        <div className="gpj-workflow__progress" aria-label={`Step ${stepIndex + 1} of ${definition.steps.length}`}>
          <span>Step {stepIndex + 1} of {definition.steps.length}</span>
          <div><span style={{ width: `${progress}%` }} /></div>
        </div>
        <h1>{currentStep.title}</h1>
        {currentStep.content_blocks.map((block, index) => (
          <p
            key={`${block.type}-${index}`}
            className={block.type === 'notice' ? 'gpj-workflow__notice' : undefined}
          >
            {block.text}
          </p>
        ))}

        {currentStep.resources?.length > 0 && (
          <section className="gpj-workflow__resources" aria-labelledby="workflow-resources-title">
            <h2 id="workflow-resources-title">Resources</h2>
            <div>
              {currentStep.resources.map(renderResource)}
            </div>
          </section>
        )}

        {error && (
          <div className="gpj-error" role="alert">
            {error}
            {conflict && (
              <button type="button" onClick={reloadLatest}>Reload latest progress</button>
            )}
          </div>
        )}

        <form onSubmit={submit} noValidate>
          {currentStep.fields.map(renderField)}
          <button
            type="submit"
            className="gpj-btn gpj-btn--primary"
            disabled={!canContinue || submitting}
          >
            {submitting ? 'Saving…' : 'Continue'}
          </button>
        </form>
      </div>
    </section>
  );
}
